import { MutationOptions } from '@apollo/client';

import { gql } from '@/__generated__/gateway';
import {
  HobbiiPatternSaveInput,
  ReadNotificationMutation,
  ReadNotificationMutationVariables,
  ReadNotificationsInput,
  ReadNotificationsMutation,
  ReadNotificationsMutationVariables,
  SeenUserNotificationsMutation,
  SeenUserNotificationsMutationVariables,
  UserPatternSaveMutation,
  UserPatternSaveMutationVariables
} from '@/__generated__/gateway/graphql';

export const markNotificationAsReadMutation = gql(`
  mutation ReadNotification($notificationId: ID!) {
    notification {
      readNotification(notification_id: $notificationId) {
        success
      }
    }
  }
`);

export const mutateReadNotification = (
  notificationId: string
): MutationOptions<
  ReadNotificationMutation,
  ReadNotificationMutationVariables
> => ({
  mutation: markNotificationAsReadMutation,
  variables: { notificationId }
});

export const markNotificationsAsReadMutation = gql(`
  mutation ReadNotifications($input: ReadNotificationsInput) {
    notification {
      readNotifications(input: $input) {
        success
      }
    }
  }
`);

export const mutateReadNotifications = (
  input: ReadNotificationsInput
): MutationOptions<
  ReadNotificationsMutation,
  ReadNotificationsMutationVariables
> => ({
  mutation: markNotificationsAsReadMutation,
  variables: { input }
});

export const seenUserNotificationsMutation = gql(`
  mutation SeenUserNotifications {
    notification {
      seenUserNotifications {
        success
      }
    }
  }
`);

export const mutateSeenUserNotifications = (): MutationOptions<
  SeenUserNotificationsMutation,
  SeenUserNotificationsMutationVariables
> => ({
  mutation: seenUserNotificationsMutation
});

export const crafterProjectCreateMutation = gql(`
  mutation CrafterProjectCreate($input: ProjectCreateInput!) {
    crafter {
      userProjectCreate(input: $input) {
        ...ListProject
      }
    }
  }
`);

export const crafterProjectFileUploadMutation = gql(`
  mutation CrafterProjectFileUpload($input: FileUploadCreateInput!) {
    crafter {
      fileUploadCreate(input: $input) {
        file_identifier
        upload_endpoint
      }
    }
  }
`);

export const crafterProjectUpdateMutation = gql(`
  mutation CrafterProjectUpdate($projectId: ID!, $input: ProjectUpdateInput) {
    crafter {
      userProjectUpdate(id: $projectId, input: $input) {
        id
        name
        craft_type
        is_public
        state
        recipient
        started_at
        ends_at
        image {
          id
          url
        }
      }
    }
  }
`);

export const crafterProjectProgressUpdateMutation = gql(`
  mutation CrafterProjectProgressUpdate($input: ProjectProgressUpdateInput!) {
    crafter {
      userProjectUpdateProgress(input: $input) {
        id
        content
        rows
        images {
          id
          url
        }
        created_at
        updated_at
      }
    }
  }
`);

export const userProjectAssignYarn = gql(`
  mutation UserProjectAssignYarn($input: ProjectAssignYarnInput!) {
    crafter {
      userProjectAssignYarn(input: $input) {
        id
        updated_at
        yarns {
          id
          quantity
          in_use
          updated_at
        }
      }
    }
  }
`);

export const createCrafterSurveyMutation = gql(`
  mutation CrafterCreateSurvey($input: SurveyResponseCreateInput!) {
    crafter {
      surveyResponseCreate(input: $input) {
        id
        survey {
          id
          name
          help_text
          questions {
            id
            text
            help_text
            required
            options {
              id
              text
              order
            }
            type
            order
          }
        }
      }
    }
  }
`);

export const saveCrafterSurveyMutation = gql(`
  mutation CrafterSaveSurvey($input: SurveyResponseSaveInput!) {
    crafter {
      surveyResponseSave(input: $input) {
        success
      }
    }
  }
`);

export const createCrafterYarnMutation = gql(`
  mutation UserYarnCreate($input: YarnCreateInput!) {
    crafter {
      userYarnCreate(input: $input) {
        ...FullYarn
      }
    }
  }
`);

export const updateCrafterYarnMutation = gql(`
  mutation UserYarnUpdate($input: YarnUpdateInput!) {
    crafter {
      userYarnUpdate(input: $input) {
        ...FullYarn
      }
    }
  }
`);

export const deleteCrafterYarnMutation = gql(`
  mutation UserYarnDelete($userYarnDeleteId: ID!) {
    crafter {
      userYarnDelete(id: $userYarnDeleteId) {
        success
        message
      }
    }
  }
`);

export const createCrafterWishlistMutation = gql(`
  mutation WishlistCreate($input: WishlistCreateInput!, $cursor: String, $limit: Int) {
    crafter {
      wishlistCreate(input: $input) {
        userErrors
        wishlist {
          created_at
          description
          id
          is_default
          is_public
          items(cursor: $cursor, limit: $limit) {
            cursor
            items {
              created_at
              id
              product_group_identifier
              sku
            }
            limit
          }
          name
          public_id
          updated_at
        }
      }
    }
  }
`);

export const deleteCrafterWishlistMutation = gql(`
  mutation WishlistDelete($wishlistDeleteId: ID!) {
    crafter {
      wishlistDelete(id: $wishlistDeleteId) {
        success
      }
    }
  }
`);

export const addItemToWishlistMutation = gql(`
  mutation AddItemToWishlist($wishlistId: ID!, $sku: String, $productGroupIdentifier: String) {
    crafter {
      wishlistAddItem(wishlistId: $wishlistId, sku: $sku, product_group_identifier: $productGroupIdentifier) {
        userErrors
        wishlistItem {
          created_at
          id
          product_group_identifier
          sku
        }
      }
    }
  }
`);

export const removeItemFromWishlistMutation = gql(`
  mutation RemoveItemFromWishlist($wishlistId: ID!, $itemId: ID!) {
    crafter {
      wishlistRemoveItem(wishlistId: $wishlistId, itemId: $itemId) {
        success
        userErrors
      }
    }
  }
`);

export const updateCrafterWishlistMutation = gql(`
  mutation WishlistUpdate($wishlistUpdateId: ID!, $input: WishlistUpdateInput!, $cursor: String, $limit: Int) {
    crafter {
      wishlistUpdate(id: $wishlistUpdateId, input: $input) {
        userErrors
        wishlist {
          id
          public_id
          name
          description
          items(cursor: $cursor, limit: $limit) {
            cursor
            items {
              created_at
              id
              product_group_identifier
              sku
            }
            limit
          }
          is_default
          is_public
          created_at
          updated_at
        }
      }
    }
  }
`);

export const userPatternSaveMutation = gql(`
  mutation UserPatternSave($input: HobbiiPatternSaveInput!) {
    crafter {
      userPatternSave(input: $input) {
        pattern {
          id
          name
        }
        userErrors
      }
    }
  }
`);

export const mutateUserPatternSave = (
  input: HobbiiPatternSaveInput
): MutationOptions<
  UserPatternSaveMutation,
  UserPatternSaveMutationVariables
> => ({
  mutation: userPatternSaveMutation,
  variables: { input }
});
